/**
 * Moeda da viagem (decisão de produto, 12/09/2026).
 *
 * Cada viagem tem UMA moeda principal, escolhida de forma explícita ao criar
 * (sem padrão) e editável só enquanto não há dados financeiros. Ela manda em
 * tudo: registro de despesas, totais, saldos, ranking, fechamento e
 * formatação. Não há conversão automática em lugar nenhum — a cotação que a
 * pessoa pagou não é a de mercado, e converter valores históricos seria
 * mentir com números. Um valor em minor units nunca muda de significado.
 *
 * O que esta lista NÃO é: um teto arquitetural. Moeda por despesa, cotação
 * manual, moeda de referência da pessoa e mais moedas entram depois como
 * dados novos, sem reinterpretar os existentes.
 */

export const TRIP_CURRENCIES = ['USD', 'BRL', 'EUR'] as const;
export type TripCurrency = (typeof TRIP_CURRENCIES)[number];

export const TRIP_CURRENCY_LABELS: Record<TripCurrency, { curto: string; nome: string; simbolo: string }> = {
  USD: { curto: 'USD', nome: 'Dólar americano', simbolo: 'US$' },
  BRL: { curto: 'BRL', nome: 'Real', simbolo: 'R$' },
  EUR: { curto: 'EUR', nome: 'Euro', simbolo: '€' },
};

export function isTripCurrency(v: unknown): v is TripCurrency {
  return typeof v === 'string' && (TRIP_CURRENCIES as readonly string[]).includes(v);
}

/** Normaliza o que veio de fora (`" usd "`) ou devolve `null` se não for suportada. */
export function parseTripCurrency(v: unknown): TripCurrency | null {
  if (typeof v !== 'string') return null;
  const limpo = v.trim().toUpperCase();
  return isTripCurrency(limpo) ? limpo : null;
}

/** Texto de apoio único — a mesma frase em criar e em editar a viagem. */
export const TRIP_CURRENCY_HELP =
  'Usada para registrar e apresentar os gastos desta viagem. O Viajandos não converte valores automaticamente entre moedas.';
export const TRIP_CURRENCY_LOCKED =
  'A viagem já tem gastos registrados nesta moeda. Trocar a moeda agora mudaria o significado dos valores existentes, e o Viajandos não converte automaticamente.';

/** Símbolo curto para campos de valor ("US$", "R$", "€"); moeda desconhecida mostra o código. */
export function simboloDaMoeda(currency: string): string {
  return isTripCurrency(currency) ? TRIP_CURRENCY_LABELS[currency].simbolo : currency;
}
